import { Pencil, Trash2, Clock, CheckCircle, XCircle } from 'lucide-react';
import type { TimeCondition } from '../../types/timeCondition';
import { WEEKDAYS } from '../../types/timeCondition';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  EmptyState,
  Badge,
} from '../ui';

interface TimeConditionListProps {
  conditions: TimeCondition[];
  onEdit: (condition: TimeCondition) => void;
  onDelete: (condition: TimeCondition) => void;
  isLoading?: boolean;
}

function TimeConditionList({ conditions, onEdit, onDelete, isLoading }: TimeConditionListProps) {
  const getWeekdayLabel = (weekday: number) => {
    return WEEKDAYS.find(day => day.value === weekday)?.label || 'Desconhecido';
  };

  const formatTimeRanges = (timeRanges: TimeCondition['time_ranges']) => {
    if (!timeRanges || timeRanges.length === 0) return 'Nenhum horário definido';

    // Group by weekday
    const groupedByWeekday = timeRanges.reduce((acc, range) => {
      if (!acc[range.weekday]) {
        acc[range.weekday] = [];
      }
      acc[range.weekday].push(`${range.start_time} - ${range.end_time}`);
      return acc;
    }, {} as Record<number, string[]>);

    // Format the output
    return Object.entries(groupedByWeekday)
      .map(([weekday, times]) => {
        const weekdayLabel = getWeekdayLabel(parseInt(weekday));
        return `${weekdayLabel}: ${times.join(', ')}`;
      })
      .join('; ');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (conditions.length === 0) {
    return (
      <EmptyState
        icon={<Clock className="h-12 w-12" />}
        title="Nenhuma condição de tempo cadastrada"
        description="Crie sua primeira condição de tempo para controlar quando seus assistentes estarão disponíveis"
      />
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Descrição</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Horários</TableHead>
            <TableHead className="relative">
              <span className="sr-only">Ações</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {conditions.map(condition => (
            <TableRow key={condition.id}>
              <TableCell>{condition.description}</TableCell>
              <TableCell>
                <Badge
                  variant={condition.active ? 'success' : 'error'}
                  className="flex items-center gap-1"
                >
                  {condition.active ? (
                    <>
                      <CheckCircle className="h-3 w-3" />
                      Ativo
                    </>
                  ) : (
                    <>
                      <XCircle className="h-3 w-3" />
                      Inativo
                    </>
                  )}
                </Badge>
              </TableCell>
              <TableCell className="max-w-md">
                <div className="truncate" title={formatTimeRanges(condition.time_ranges)}>
                  {formatTimeRanges(condition.time_ranges)}
                </div>
              </TableCell>
              <TableCell className="text-right space-x-2">
                <Button variant="ghost" onClick={() => onEdit(condition)} title="Editar">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" onClick={() => onDelete(condition)} title="Excluir">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default TimeConditionList;
